import { gql } from "@apollo/client";
import { faPen, faRefresh, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import { ComponentStateTypes } from "components/common/ComponentState";
import DeleteConfirmationModal from "components/common/confirmation/DeleteConfirmationModal";
import Error from "components/common/Error";
import Loading from "components/common/loading/Loading";
import NoData from "components/common/NoData";
import PropertyView from "components/common/views/PropertyView";
import ModalPortal from "components/modal/ModalPortal";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import useAppContext from "utilities/hooks/useAppContext";
import WalletTransactionFrom from "./WalletTransactionFrom";

type WalletTransactionViewModel = {
    walletId: number;
    id: number;
    amount: any;
    transactionDate: any;
    transactionId?: string | null;
    note?: string | null;
}

type WalletTransactionViewProps = {
    model?: WalletTransactionViewModel | number
    onDelete?: () => void;
    onUpdated?: () => void;
}

const query = gql`
query GetTransactionsView($id: Int!) {
  walletTransaction(id: $id) {
    walletId
    id,
    amount
    transactionDate,
    transactionId,
    note
  }
}
`;

const WalletTransactionView = (props: WalletTransactionViewProps) => {
    const DELETE_MODAL_HASH = `wallet-transaction-view-delete-${typeof props.model == 'number' ? props.model :  props.model?.id}`;
    const FORM_MODAL_HASH = `wallet-transaction-view-form-${typeof props.model == 'number' ? props.model :  props.model?.id}`;
    const [model, setModel] = useState<WalletTransactionViewModel | undefined | null>(typeof props.model == 'number' ? null : props.model);
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const context = useAppContext();

    useEffect(() => {
        if (typeof props.model == 'number') {
            loadData().then();
        } else {
            setComponentState('');
        }
    }, [props.model]);

    const getId = () => {
        return typeof props.model == 'number' ? props.model : props.model!.id;
    }

    const loadData = async () => {
        if(componentState !== 'loading') {
            setComponentState('loading');
        }
        try {
            const request = await graph({
                query: query,
                variables: {
                    id: getId()
                }
            });

            if (request.errors) {
                throw request.errors;
            }

            setModel({
                ...request.data.walletTransaction,
                transactionDate: new Date(request.data.walletTransaction.transactionDate)
            });
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 't1', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    const update = async () => {
        await loadData();
        props.onUpdated?.();
    }

    if (!props.model) return <NoData />;

    if(componentState === 'loading') return <Loading />;
    if(componentState === 'error') return <Error message="error" />;

    return <>
        <div>
            <div>
                <PropertyView label='fields.date.name' value={model!.transactionDate} propertyType="datetime" />
                <PropertyView label='fields.amount.name' value={model!.amount} propertyType="money" />
                <PropertyView label='fields.transaction-id.name' value={model!.transactionId} />
                <PropertyView label='fields.note.name' value={model!.note} propertyType="note" />
            </div>
            <hr />
            <div className="grid grid-cols-3 gap-5 w-full pt-5 text-right">
                <Button title="button.reload" theme="link" onClick={loadData}>
                    <FontAwesomeIcon icon={faRefresh} />
                </Button>
                <Button title="button.edit" theme="link primary" onClick={() => context.modal.open(FORM_MODAL_HASH)}>
                    <FontAwesomeIcon icon={faPen} />
                </Button>
                <Button title="button.delete" theme="link" onClick={() => context.modal.open(DELETE_MODAL_HASH)}>
                    <FontAwesomeIcon icon={faTrash} className="text-danger" />
                </Button>
            </div>
        </div>
        <DeleteConfirmationModal
            name={DELETE_MODAL_HASH}
            onConfirm="walletTransactionDelete"
            id={getId()}
            onAfterConfirmationExecution={props.onDelete}
        />
        <ModalPortal name={FORM_MODAL_HASH} title="common.edit"  >
            <WalletTransactionFrom walletId={model!.walletId} model={model || undefined} onSave={update} />
        </ModalPortal>
    </>
}

export default WalletTransactionView;
